import { supabase } from '../utils/supabase';
import emailService from '../utils/emailService';
import { replaceTemplateVariables, getTemplateById } from '../constants/emailTemplates';

/**
 * Campaign Service
 * Handles all campaign-related operations including creation, management, and email sending
 */

class CampaignService {
  /**
   * Send a test email for a campaign
   */
  async sendTestEmail(testEmailData) {
    try {
      const { email, subject, templateId, content } = testEmailData;

      // Get template and generate HTML content
      const template = getTemplateById(templateId);
      if (!template) {
        throw new Error('Template not found');
      }

      // Replace template variables
      const htmlContent = replaceTemplateVariables(template.htmlTemplate, {
        firstName: 'Test User',
        appUrl: 'https://dashboard.vasprouts.com',
        ...content
      });

      // Send test email
      const result = await emailService.sendMarketingEmail({
        to: email,
        subject: subject,
        htmlContent: htmlContent
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      return { success: true, message: 'Test email sent successfully' };
    } catch (error) {
      console.error('[CampaignService] Error sending test email:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create a new campaign
   */
  async createCampaign(campaignData) {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .insert({
          title: campaignData.title,
          subject: campaignData.subject,
          template_id: campaignData.templateId,
          content: campaignData.content || {},
          created_by: campaignData.createdBy,
          status: campaignData.scheduledAt ? 'scheduled' : 'draft',
          scheduled_at: campaignData.scheduledAt || null,
          filters: campaignData.filters || {}
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[CampaignService] Error creating campaign:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all campaigns
   */
  async getCampaigns(filters = {}) {
    try {
      let query = supabase
        .from('campaigns')
        .select(`
          *,
          created_by_profile:user_profiles!campaigns_created_by_fkey(id, full_name, email)
        `)
        .order('created_at', { ascending: false });

      // Apply status filter if provided
      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[CampaignService] Error fetching campaigns:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get campaign by ID
   */
  async getCampaignById(campaignId) {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select(`
          *,
          created_by_profile:user_profiles!campaigns_created_by_fkey(id, full_name, email),
          recipients:campaign_recipients(
            id,
            user_id,
            status,
            sent_at,
            error_message,
            user:user_profiles(id, full_name, email)
          )
        `)
        .eq('id', campaignId)
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[CampaignService] Error fetching campaign:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update campaign
   */
  async updateCampaign(campaignId, updates) {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .update(updates)
        .eq('id', campaignId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[CampaignService] Error updating campaign:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete campaign
   */
  async deleteCampaign(campaignId) {
    try {
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', campaignId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('[CampaignService] Error deleting campaign:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get eligible recipients based on filters
   */
  async getEligibleRecipients(filters = {}) {
    try {
      let query = supabase
        .from('user_profiles')
        .select('id, email, full_name, role, created_at, notification_settings');

      // Filter by marketing email opt-in
      query = query.not('notification_settings', 'is', null);

      // Apply role filter
      if (filters.role && filters.role !== 'all') {
        query = query.eq('role', filters.role);
      }

      // Apply date range filter
      if (filters.createdAfter) {
        query = query.gte('created_at', filters.createdAfter);
      }
      if (filters.createdBefore) {
        query = query.lte('created_at', filters.createdBefore);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Filter by marketing emails preference (client-side due to JSONB)
      const eligibleRecipients = data.filter(user => {
        const marketingEnabled = user.notification_settings?.email?.marketingEmails === true;
        return marketingEnabled;
      });

      return { success: true, data: eligibleRecipients };
    } catch (error) {
      console.error('[CampaignService] Error fetching eligible recipients:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Add recipients to campaign
   */
  async addRecipientsToCampaign(campaignId, userIds) {
    try {
      const recipients = userIds.map(userId => ({
        campaign_id: campaignId,
        user_id: userId,
        status: 'pending'
      }));

      const { data, error } = await supabase
        .from('campaign_recipients')
        .insert(recipients)
        .select();

      if (error) throw error;

      // Update campaign recipient count
      await this.updateCampaign(campaignId, {
        recipient_count: userIds.length
      });

      return { success: true, data };
    } catch (error) {
      console.error('[CampaignService] Error adding recipients:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send campaign emails
   */
  async sendCampaign(campaignId) {
    try {
      // Get campaign details
      const campaignResult = await this.getCampaignById(campaignId);
      if (!campaignResult.success) {
        throw new Error('Failed to fetch campaign');
      }

      const campaign = campaignResult.data;
      
      // Update campaign status to sending
      await this.updateCampaign(campaignId, { status: 'sending' });

      // Get template
      const template = getTemplateById(campaign.template_id);
      if (!template) {
        throw new Error('Template not found');
      }

      let sentCount = 0;
      let failedCount = 0;

      // Send emails to all pending recipients
      const pendingRecipients = campaign.recipients.filter(r => r.status === 'pending');

      for (const recipient of pendingRecipients) {
        try {
          // Prepare template variables
          const variables = {
            firstName: recipient.user.full_name.split(' ')[0],
            appUrl: window.location.origin,
            ...campaign.content
          };

          // Replace variables in template
          const htmlContent = replaceTemplateVariables(template.htmlTemplate, variables);
          const subject = replaceTemplateVariables(campaign.subject, variables);

          // Send email using existing email service
          const emailResult = await emailService.sendEmail('marketing_campaign', {
            to: recipient.user.email,
            subject: subject,
            htmlContent: htmlContent
          });

          if (emailResult.success) {
            // Update recipient status to sent
            await supabase
              .from('campaign_recipients')
              .update({
                status: 'sent',
                sent_at: new Date().toISOString()
              })
              .eq('id', recipient.id);

            sentCount++;
          } else {
            throw new Error(emailResult.error || 'Failed to send email');
          }
        } catch (error) {
          console.error(`[CampaignService] Error sending to ${recipient.user.email}:`, error);
          
          // Update recipient status to failed
          await supabase
            .from('campaign_recipients')
            .update({
              status: 'failed',
              error_message: error.message
            })
            .eq('id', recipient.id);

          failedCount++;
        }

        // Add small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Update campaign with final stats
      await this.updateCampaign(campaignId, {
        status: 'sent',
        sent_at: new Date().toISOString(),
        sent_count: sentCount,
        failed_count: failedCount
      });

      // Record metrics
      await this.recordMetric(campaignId, 'emails_sent', sentCount);
      if (failedCount > 0) {
        await this.recordMetric(campaignId, 'emails_failed', failedCount);
      }

      return {
        success: true,
        data: {
          sentCount,
          failedCount,
          totalRecipients: pendingRecipients.length
        }
      };
    } catch (error) {
      console.error('[CampaignService] Error sending campaign:', error);
      
      // Update campaign status to draft on error
      await this.updateCampaign(campaignId, { status: 'draft' });
      
      return { success: false, error: error.message };
    }
  }

  /**
   * Schedule campaign for later sending
   */
  async scheduleCampaign(campaignId, scheduledAt) {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .update({
          status: 'scheduled',
          scheduled_at: scheduledAt
        })
        .eq('id', campaignId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[CampaignService] Error scheduling campaign:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Cancel scheduled campaign
   */
  async cancelCampaign(campaignId) {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .update({
          status: 'cancelled'
        })
        .eq('id', campaignId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[CampaignService] Error cancelling campaign:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Record campaign metric
   */
  async recordMetric(campaignId, metricType, metricValue, metadata = {}) {
    try {
      const { data, error } = await supabase
        .from('campaign_metrics')
        .insert({
          campaign_id: campaignId,
          metric_type: metricType,
          metric_value: metricValue,
          metadata
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[CampaignService] Error recording metric:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get campaign metrics
   */
  async getCampaignMetrics(campaignId) {
    try {
      const { data, error } = await supabase
        .from('campaign_metrics')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[CampaignService] Error fetching metrics:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get campaign statistics
   */
  async getCampaignStats() {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select('status, sent_count, failed_count, recipient_count');

      if (error) throw error;

      const stats = {
        totalCampaigns: data.length,
        draftCampaigns: data.filter(c => c.status === 'draft').length,
        scheduledCampaigns: data.filter(c => c.status === 'scheduled').length,
        sentCampaigns: data.filter(c => c.status === 'sent').length,
        totalEmailsSent: data.reduce((sum, c) => sum + (c.sent_count || 0), 0),
        totalEmailsFailed: data.reduce((sum, c) => sum + (c.failed_count || 0), 0),
        totalRecipients: data.reduce((sum, c) => sum + (c.recipient_count || 0), 0)
      };

      return { success: true, data: stats };
    } catch (error) {
      console.error('[CampaignService] Error fetching campaign stats:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new CampaignService();
